/**
 * Home page feature section content, editable from the admin panel.
 *
 * Bare axios client for the same reason `authApi` uses one: `service/axios`
 * carries an interceptor that treats 401 as "session expired" and bounces to the
 * home page. This is a public read made by signed-out visitors, so that would be
 * actively wrong here.
 */

import axios from 'axios';

import service from './constant';

const homeFeatureClient = axios.create({
    baseURL: service.API_URL + '/api/v1',
    headers: { 'Content-Type': 'application/json' }
});

export interface HomeFeature {
    id: string;
    /** Which part of the page this belongs to: 'features' or 'app_promo'. */
    section: string;
    sortOrder: number;
    title: string;
    highlight: string | null;
    description: string | null;
    /** Admin upload, absolute. Null when nothing has been uploaded for this item. */
    imageUrl: string | null;
    /** Filename of an image bundled in this build, e.g. 'explore-1.png'. */
    imageKey: string | null;
}

/**
 * Never throws and never returns a partial section.
 *
 * An empty array means "use what is compiled in". The section is above the fold
 * on the home page, so a slow or failed content call must not be able to empty
 * it — the caller keeps its hardcoded copy as the floor and only swaps in
 * remote content when a usable payload arrives.
 *
 * The timeout matters more than it looks: without one, an unreachable API does
 * not fail, it hangs, and the section would sit empty for as long as the socket
 * stays open.
 */
export const fetchHomeFeatures = async (): Promise<HomeFeature[]> => {
    try {
        const { data } = await homeFeatureClient.get('/home-feature', { timeout: 4000 });
        const items: HomeFeature[] = Array.isArray(data?.items) ? data.items : [];
        // An item with no title would render as a blank column.
        return items.filter((item) => item && item.title);
    } catch {
        return [];
    }
};

/**
 * Just the rows for one section of the page, in order.
 *
 * Callers must filter: the endpoint returns every section in one response, so a
 * component that maps the whole payload would render another section's rows as
 * its own — the feature grid would sprout a sixth column reading "Meet the
 * INSTAACARD App".
 */
export const fetchHomeFeatureSection = async (section: string): Promise<HomeFeature[]> =>
    (await fetchHomeFeatures()).filter((item) => item.section === section);
